import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

const SLICE_NAME = 'evaluations'

function prepConfig(getState: any) {
	const {
		userLogin: { userInfo },
	} = getState()
	return { headers: { Authorization: `Bearer ${userInfo.token}` } }
}

const initialState = {
	entities: {} as Record<string, any>,
	ids: [] as string[],
	isLoading: false,
	isInitialized: false,
	error: null as string | null,
}

export const initializeEvaluations = createAsyncThunk(
	`${SLICE_NAME}/initialize`,
	async (params: { status?: string; search?: string } | undefined, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get('/api/evaluations', {
				...prepConfig(getState),
				params,
			})
			return data
		} catch {
			return rejectWithValue('Failed to load evaluations')
		}
	},
)

export const fetchEvaluationById = createAsyncThunk(
	`${SLICE_NAME}/fetchById`,
	async (id: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(`/api/evaluations/${id}`, prepConfig(getState))
			return data
		} catch {
			return rejectWithValue('Failed to load evaluation')
		}
	},
)

// Read-only per-tab summary (Sites → Approvals) for the edit drawer. No
// reducer — callers read the returned payload directly, so it never touches
// the entities cache.
export const fetchEvaluationSummary = createAsyncThunk(
	`${SLICE_NAME}/fetchSummary`,
	async (id: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(`/api/evaluations/${id}/summary`, prepConfig(getState))
			return data
		} catch {
			return rejectWithValue('Failed to load summary')
		}
	},
)

export const createEvaluation = createAsyncThunk(
	`${SLICE_NAME}/create`,
	async (
		payload: { name: string; description?: string; marketArea?: string; portfolio?: string; seedFromPortfolio?: boolean },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.post('/api/evaluations', payload, prepConfig(getState))
			return data
		} catch (err: any) {
			// Surface 409 conflict with existingEvaluationId so the UI can offer "Open Evaluation"
			if (err?.response?.status === 409) {
				return rejectWithValue({
					status: 409,
					message: err.response.data?.message || 'Evaluation already exists for this portfolio',
					existingEvaluationId: err.response.data?.existingEvaluationId,
					existingEvaluationName: err.response.data?.existingEvaluationName,
				})
			}
			return rejectWithValue({ status: err?.response?.status, message: 'Failed to create evaluation' })
		}
	},
)

export const fetchEvaluationByPortfolioId = createAsyncThunk(
	`${SLICE_NAME}/fetchByPortfolioId`,
	async (portfolioId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get('/api/evaluations', {
				...prepConfig(getState),
				params: { portfolio: portfolioId },
			})
			// API returns an array; an unlinked portfolio yields [].
			return data[0] || null
		} catch {
			return rejectWithValue('Failed to look up evaluation for portfolio')
		}
	},
)

export const createEvaluationFromPortfolio = createAsyncThunk(
	`${SLICE_NAME}/createFromPortfolio`,
	async (
		payload: { portfolioId: string; name: string; description?: string; marketArea?: string },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.post(
				'/api/evaluations',
				{
					name: payload.name,
					description: payload.description,
					marketArea: payload.marketArea,
					portfolio: payload.portfolioId,
					seedFromPortfolio: true,
				},
				prepConfig(getState),
			)
			return data
		} catch (err: any) {
			if (err?.response?.status === 409) {
				return rejectWithValue({
					status: 409,
					message: err.response.data?.message || 'Evaluation already exists for this portfolio',
					existingEvaluationId: err.response.data?.existingEvaluationId,
					existingEvaluationName: err.response.data?.existingEvaluationName,
				})
			}
			return rejectWithValue({ status: err?.response?.status, message: 'Failed to create evaluation' })
		}
	},
)

export const syncEvaluationWithPortfolio = createAsyncThunk(
	`${SLICE_NAME}/syncWithPortfolio`,
	async (evaluationId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post(
				`/api/evaluations/${evaluationId}/sync-portfolio`,
				{},
				prepConfig(getState),
			)
			return { evaluationId, ...data }
		} catch {
			return rejectWithValue('Failed to sync evaluation with portfolio')
		}
	},
)

export const updateEvaluation = createAsyncThunk(
	`${SLICE_NAME}/update`,
	async (payload: { _id: string; [key: string]: any }, { getState, rejectWithValue }) => {
		try {
			const { _id, ...body } = payload
			const { data } = await axios.put(`/api/evaluations/${_id}`, body, prepConfig(getState))
			return data
		} catch (err: any) {
			// Forward the server's message: this endpoint rejects invalid status
			// transitions, and an APPROVED attempt with no winning site, both of
			// which the user needs to read to know what to fix.
			return rejectWithValue(
				err?.response?.data?.message || 'Failed to update evaluation',
			)
		}
	},
)

/**
 * Set or clear the evaluation's winning site. `siteId: null` clears it.
 *
 * The server rejects this once the evaluation is APPROVED or ARCHIVED,
 * because the selected site has already been promoted into the Locations
 * catalogue by then. Callers should await the result rather than updating
 * optimistically, so a rejected change never leaves a phantom selection on
 * screen.
 */
export const setSelectedSite = createAsyncThunk(
	`${SLICE_NAME}/setSelectedSite`,
	async (
		payload: { evalId: string; siteId: string | null },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.put(
				`/api/evaluations/${payload.evalId}/selected-site`,
				{ siteId: payload.siteId },
				prepConfig(getState),
			)
			return data
		} catch (err: any) {
			return rejectWithValue(
				err?.response?.data?.message || 'Failed to set the winning site',
			)
		}
	},
)

/**
 * Soft-delete an evaluation. The server refuses with 409 while the
 * evaluation still has sites, so the rejection message is the useful part —
 * it names how many are in the way — and callers should surface it rather
 * than a generic failure.
 */
export const deleteEvaluation = createAsyncThunk(
	`${SLICE_NAME}/delete`,
	async (id: string, { getState, rejectWithValue }) => {
		try {
			await axios.delete(`/api/evaluations/${id}`, prepConfig(getState))
			return id
		} catch (err: any) {
			return rejectWithValue(
				err?.response?.data?.message || 'Failed to delete evaluation',
			)
		}
	},
)

/**
 * Merge a single evaluation into the cache without losing the fields only
 * the LIST endpoint returns.
 *
 * The detail, update, and selected-site endpoints all return the same
 * narrower shape — no `siteAvatars`, no `siteCount`, no `stats` — so a
 * naive replace would strip the avatars from a cached entity after any one
 * of them. Shared by all three reducers so they cannot drift apart.
 */
function mergeEvaluation(state: typeof initialState, payload: any) {
	const existing = state.entities[payload._id]
	state.entities[payload._id] = {
		...payload,
		siteAvatars: payload.siteAvatars ?? existing?.siteAvatars,
		siteCount: payload.siteCount ?? existing?.siteCount,
		stats: payload.stats ?? existing?.stats,
	}
	if (!state.ids.includes(payload._id)) {
		state.ids.push(payload._id)
	}
}

const evaluationSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(initializeEvaluations.pending, (state) => {
				state.isLoading = true
				state.error = null
			})
			.addCase(initializeEvaluations.fulfilled, (state, action) => {
				const evaluations = action.payload
				// Carry `stats` across the wipe. The list endpoint doesn't return
				// it, and the detail shell fires this alongside fetchEvaluationById
				// on a deep link — so without this the Overview counters would
				// render real numbers and then revert to zero when the (heavier)
				// list request lands second.
				const priorStats: Record<string, any> = {}
				for (const id of state.ids) {
					if (state.entities[id]?.stats) priorStats[id] = state.entities[id].stats
				}
				state.entities = {}
				state.ids = []
				for (const eval_ of evaluations) {
					state.entities[eval_._id] = {
						...eval_,
						stats: eval_.stats ?? priorStats[eval_._id],
					}
					state.ids.push(eval_._id)
				}
				state.isLoading = false
				state.isInitialized = true
			})
			.addCase(initializeEvaluations.rejected, (state, action) => {
				state.isLoading = false
				state.error = action.payload as string
			})
			.addCase(fetchEvaluationById.fulfilled, (state, action) => {
				mergeEvaluation(state, action.payload)
			})
			.addCase(createEvaluation.fulfilled, (state, action) => {
				const eval_ = action.payload
				state.entities[eval_._id] = eval_
				state.ids.unshift(eval_._id)
			})
			.addCase(createEvaluationFromPortfolio.fulfilled, (state, action) => {
				const eval_ = action.payload
				state.entities[eval_._id] = eval_
				state.ids.unshift(eval_._id)
			})
			.addCase(updateEvaluation.fulfilled, (state, action) => {
				mergeEvaluation(state, action.payload)
			})
			.addCase(setSelectedSite.fulfilled, (state, action) => {
				mergeEvaluation(state, action.payload)
			})
			.addCase(deleteEvaluation.fulfilled, (state, action) => {
				const id = action.payload
				delete state.entities[id]
				state.ids = state.ids.filter((i) => i !== id)
			})
	},
})

export default evaluationSlice
